
import React, { useMemo } from 'react';
import { Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InvoiceStatus, INVOICE_STATUS_COLORS } from './invoiceStatus.constants';
import { toTitleCase } from '../../../utils/commonUtils';

/**
 * StatusChipProps - A robust, reusable, and mobile-friendly status chip for any status use-case.
 *
 * @param status - The status string to display (e.g., 'Billed', 'Draft', etc.)
 * @param count - Optional count to display alongside the status
 * @param labelFormatter - Optional function to customize the label rendering
 * @param icon - Optional icon to display in the chip
 * @param tooltip - Optional tooltip for extra info
 * @param ariaLabel - Optional ARIA label for accessibility
 * @param className - Optional className for custom styling
 * @param style - Optional style object for custom inline styles
 * @param width - Optional width (responsive by default)
 * @param height - Optional height (default 28)
 */
export interface StatusChipProps {
  status: InvoiceStatus | string;
  count?: number;
  labelFormatter?: (status: string, count?: number) => React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: string;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties; // Deprecated: use sx instead
  sx?: object; // MUI sx prop for custom styles
  width?: string | number;
  height?: string | number;
  onClick?: (status: string) => void; // Called when chip is clicked
  selected?: boolean; // Visual selected state
  // Accessibility passthrough props for consumers
  role?: React.AriaRole;
  tabIndex?: number;
  /** Support ARIA pressed state on custom component */
  ['aria-pressed']?: boolean;
  /** Support test id passthrough */
  ['data-testid']?: string;
}


// Generate a visually distinct color for unknown statuses
function getStatusStyles(status: string) {
  const normalizedStatus = toTitleCase(status);
  if (INVOICE_STATUS_COLORS[normalizedStatus as InvoiceStatus]) return INVOICE_STATUS_COLORS[normalizedStatus as InvoiceStatus];
  // Fallback: generate color from string hash
  const hash = Array.from(status).reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const color = `hsl(${hash % 360}, 60%, 40%)`;
  const background = `hsl(${hash % 360}, 70%, 95%)`;
  const border = color;
  return { color, background, border };
}


/**
 * StatusChip - A robust, reusable, and mobile-friendly status chip for any status use-case.
 *
 * Example usage:
 *   <StatusChip status="Billed" count={5} tooltip="Billed invoices" />
 */
const StatusChip: React.FC<StatusChipProps> = (props) => {
  const {
    status,
    count,
    labelFormatter,
    icon,
    tooltip,
    ariaLabel,
    className,
    style, // Deprecated
    sx,
    width,
    height = 28,
    onClick,
    selected = false,
  } = props;
  // Optional passthrough attributes
  const role = props.role;
  const tabIndex = props.tabIndex;
  const ariaPressed = props['aria-pressed'];
  const dataTestId = props['data-testid'];
  const { t : tBilling } = useTranslation('billing_config');
  
  // Status translation key mapping
  const STATUS_TRANSLATION_KEYS: Record<string, string> = {
    'Billed': 'status.billed',
    'Unsettle': 'status.unsettle',
    'Unsettled': 'status.unsettled',
    'Unbilled': 'status.unbilled',
    'Draft': 'status.draft',
    'Due': 'status.due',
    'Overdue': 'status.overdue',
    'Ordered': 'status.ordered',
    'Unordered': 'status.unordered',
    'Dispensed': 'status.dispensed',
    'Paid': 'status.paid',
    'Unpaid': 'status.unpaid',
    'Partially Paid': 'status.partially_paid',
    'SERVICE': 'status.service',
    'PHARMACY': 'status.pharmacy',
  };
  
  // Normalize status for consistent lookup
  const normalizedStatusForLookup = toTitleCase(status);
  
  // Get translated status label
  const translatedStatus = STATUS_TRANSLATION_KEYS[normalizedStatusForLookup]
    ? tBilling(STATUS_TRANSLATION_KEYS[normalizedStatusForLookup], { defaultValue: normalizedStatusForLookup })
    : normalizedStatusForLookup;
  
  // Memoize style for performance
  const { color, background, border } = useMemo(() => getStatusStyles(status), [status]);
  // Allow custom label formatting, fallback to default
  const label = labelFormatter
    ? labelFormatter(translatedStatus, count)
    : count !== undefined
      ? `${translatedStatus} : ${String(count).padStart(2, '0')}`
      : translatedStatus;
  // Only pass icon if it's a valid ReactElement (MUI Chip expects ReactElement or undefined)
  const chip = (
    <Chip
      label={label}
      variant={selected ? 'filled' : 'outlined'}
      size="small"
      {...(icon && React.isValidElement(icon) ? { icon } : {})}
      aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
      className={className}
      clickable={!!onClick}
      onClick={onClick ? () => onClick(status) : undefined}
      role={role}
      tabIndex={tabIndex}
      {...(ariaPressed !== undefined && { 'aria-pressed': ariaPressed })}
      {...(dataTestId && { 'data-testid': dataTestId })}
      sx={{
        border: '1.5px solid',
        borderColor: border,
        fontFamily: 'Nunito',
        fontSize: { xs: '12px', sm: '13px' },
        fontWeight: 700,
        lineHeight: '13px',
        letterSpacing: '0.04em',
        color: selected ? '#fff' : color,
        backgroundColor: selected ? color : background,
        width: width || 'auto',
        height: height,
        borderRadius: '16px',
        px: { xs: 0.5, sm: 1 },
        mr: 1,
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        boxShadow: selected
          ? '0 2px 8px rgba(58,128,226,0.18)'
          : '0 1px 4px rgba(0,0,0,0.08)',
        transition: 'background 0.2s, color 0.2s, box-shadow 0.2s, transform 0.15s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          backgroundColor: selected
            ? color
            : 'rgba(58, 128, 226, 0.12)',
          color: selected ? '#fff' : color,
          boxShadow: '0 4px 12px rgba(58,128,226,0.18)',
          transform: 'scale(1.04)',
        },
        '&:focus-visible': {
          outline: '2px solid #3a80e2',
          outlineOffset: '2px',
        },
        ...(sx || {}),
        ...(style || {}),
      }}
    />
  );
  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      <span style={{ display: 'inline-flex' }}>{chip}</span>
    </Tooltip>
  ) : chip;
};

export default StatusChip;
